/**
 * One-tap reasons for overriding the parts gate (#1766, decision 4).
 *
 * The server still requires a reason and still ledgers it on the blocking PO
 * and as `job.dispatch_override`; only how the office gives it changes. On the
 * classic Grid it is a free-text box, and in four weeks it was filled with
 * "Dispatch Anyways" 74 times — a ritual, not a reason. These say what
 * actually happened, and "Other…" still lets the office write one.
 */
import { choiceDialog, promptDialog } from "@/components/ui/app-dialogs";

export const PARTS_OVERRIDE_REASONS: Array<{ value: string; label: string; description: string }> = [
    {
        value: "Parts are on the truck",
        label: "Parts are on the truck",
        description: "The tech already has them; the PO just isn't marked received.",
    },
    {
        value: "Test only, no parts needed for this visit",
        label: "Test only — no parts needed",
        description: "This visit doesn't use the parts on order.",
    },
    {
        value: "Parts pickup on the way to the job",
        label: "Tech picks them up on the way",
        description: "Parts are at the supplier and ready.",
    },
];

/** Resolves the reason to send, or null when the office backed out. */
export async function pickPartsOverrideReason(): Promise<string | null> {
    const picked = await choiceDialog({
        title: "Dispatch anyway — why?",
        message: "Parts for this job aren't received yet. Pick the reason; it's saved on the PO.",
        choices: [
            ...PARTS_OVERRIDE_REASONS.map((r) => ({ value: r.value, label: r.label, description: r.description })),
            {
                value: "__other",
                label: "Other…",
                description: "Write the reason yourself.",
                variant: "outline" as const,
            },
        ],
        cancelLabel: "Don't dispatch",
    });
    if (!picked) return null;
    if (picked !== "__other") return picked;
    const typed = await promptDialog({
        title: "Dispatch anyway — why?",
        label: "Reason (saved on the PO)",
        required: true,
    });
    return typed?.trim() || null;
}
